import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import Shell, { StatCard, Section } from "@/pages/Shell";
import { api, formatError } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { LeadsBoard } from "@/pages/marketing/MarketingDashboard";
import { TasksAI } from "@/pages/constructor/ConstructorDashboard";
import { AlertsBoard } from "@/components/NextBestAction";
import { DashboardActionZone } from "@/components/ActionCenter";
import { useFocusRow } from "@/hooks/useFocusRow";
import AssignInvestorModal from "@/components/admin/AssignInvestorModal";
import { EstadoGeneralDeObraPanel } from "@/components/construction/ConstructionProgressPublisher";
import DocumentFileInput from "@/components/admin/DocumentFileInput";
import { paymentLabel, useDuplicatePayments } from "@/components/admin/operaciones/PagosTab";
import {
  ChartPieSlice, Users, House, Funnel, ListChecks, FileText,
  UploadSimple, Plus, Coin, EnvelopeSimple, WhatsappLogo, GoogleLogo,
  Pulse, Archive, CaretRight, Sparkle, Bell, PencilSimple, UserPlus, X
} from "@phosphor-icons/react";

const TABS = [
  { key: "overview", label: "Panel ejecutivo", icon: ChartPieSlice },
  { key: "alerts", label: "Alertas", icon: Bell },
  { key: "leads", label: "CRM / Leads", icon: Funnel },
  { key: "villas", label: "Villas", icon: House },
  { key: "dataroom", label: "Data room", icon: Archive },
  { key: "activity", label: "Actividad", icon: Pulse },
  { key: "users", label: "Usuarios", icon: Users },
  { key: "payments", label: "Pagos inversores", icon: Coin },
  { key: "integrations", label: "Integraciones", icon: EnvelopeSimple },
  { key: "tasks", label: "Tareas IA", icon: ListChecks },
];
const ACCENT = "#1C1C1E";

export { ExecOverview, VillasAdmin, DataRoomAdmin, ActivityLog, UsersAdmin, PaymentsAdmin, Integrations };

function ExecOverview({ onNavigate }) {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/stats/overview").then(r => setStats(r.data)).catch(() => {}); }, []);

  if (!stats) return <div className="text-stone-400">Cargando…</div>;

  const soldPct = stats.villas.total ? (stats.villas.sold / stats.villas.total * 100) : 0;

  return (
    <div>
      <div className="mb-8 fade-up">
        <div className="text-xs tracking-[0.3em] uppercase text-stone-500 mb-1">Panel ejecutivo</div>
        <h1 className="font-serif-dv text-5xl">Hola, equipo Dolce.</h1>
        <p className="text-stone-500 mt-2">Un vistazo global a todo lo que sucede hoy.</p>
      </div>

      {/* Action Center + Prioridad IA — the daily driver.
          Replaces the standalone NextBestAction widget: one hook
          drives both the top-3 AI ranking and the compact action
          board so the same data never gets fetched twice. Admin's
          "Ver todo" targets the dedicated /actions route. */}
      <DashboardActionZone lang="es" onViewAll={() => navigate("/actions")} />

      <Section title="Indicadores clave">
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatCard label="Villas vendidas" value={`${stats.villas.sold}/${stats.villas.total}`} sub={`${soldPct.toFixed(0)}% del total`} accent={ACCENT} testid="stat-villas-sold" />
          <StatCard label="Leads activos" value={stats.leads.total} sub={`${stats.leads.new} nuevos`} accent={ACCENT} testid="stat-leads-active" />
          <StatCard label="Inversores" value={stats.investors} accent={ACCENT} testid="stat-investors" />
          <StatCard label="Pagos pendientes" value={stats.pending_payments} accent={ACCENT} testid="stat-payments-pending" />
        </div>
      </Section>

      <div className="grid lg:grid-cols-2 gap-6">
        <div className="dv-card">
          <div className="text-xs tracking-widest uppercase text-stone-500 mb-4">Leads por origen</div>
          {Object.entries(stats.leads.by_source).map(([src, c]) => {
            const pct = stats.leads.total ? (c / stats.leads.total * 100) : 0;
            return (
              <div key={src} className="mb-3">
                <div className="flex justify-between text-sm mb-1">
                  <span className="capitalize">{src}</span><span className="text-stone-500">{c}</span>
                </div>
                <div className="h-2 bg-stone-100 rounded-full"><div className="h-full rounded-full bg-stone-800" style={{ width: `${pct}%` }} /></div>
              </div>
            );
          })}
        </div>
        <div className="dv-card">
          <div className="text-xs tracking-widest uppercase text-stone-500 mb-4">Funnel de estados</div>
          {Object.entries(stats.leads.by_status).map(([s, c]) => (
            <div key={s} className="flex justify-between py-2 border-b border-stone-100 text-sm">
              <span className="capitalize">{s}</span>
              <span className="font-serif-dv text-lg">{c}</span>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function VillasAdmin() {
  const navigate = useNavigate();
  const [villas, setVillas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", model: "Dolce Vistas", price_eur: 99000, status: "available", description: "" });
  const [editingBaselineFor, setEditingBaselineFor] = useState(null);
  const [assigningFor, setAssigningFor] = useState(null);
  const [ownerNames, setOwnerNames] = useState({}); // user_id → name

  const load = () => api.get("/villas").then(r => setVillas(r.data));
  useEffect(() => { load(); }, []);
  useFocusRow([villas]);

  // Resolve owner names once villas load (one /users fetch is enough — small list)
  useEffect(() => {
    const ownerIds = [...new Set(villas.map(v => v.owner_user_id).filter(Boolean))];
    if (ownerIds.length === 0) return;
    api.get("/users").then(r => {
      const map = {};
      for (const u of r.data) {
        if (ownerIds.includes(u.user_id)) map[u.user_id] = u.name;
      }
      setOwnerNames(map);
    }).catch(() => {});
  }, [villas]);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/villas", { ...form, price_eur: Number(form.price_eur) });
      setShowForm(false); load();
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <Section title="Portafolio de villas" subtitle="Click en cualquier villa para abrir su vista 360." action={
      <button onClick={() => setShowForm(!showForm)} className="dv-btn dv-btn-primary" data-testid="villa-new-btn"><Plus size={16} /> Nueva villa</button>
    }>
      {/* Avance general del complejo — la barra principal que ven todos los
          inversores. El de cada villa se edita en su ficha (Progreso obra). */}
      <EstadoGeneralDeObraPanel />
      {showForm && (
        <form onSubmit={save} className="dv-card mb-5 grid sm:grid-cols-2 gap-3">
          <input className="dv-input" placeholder="Nombre" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} required />
          <select className="dv-input" value={form.model} onChange={e => setForm({ ...form, model: e.target.value })}>
            <option>Dolce Vistas</option><option>Dolce Privè</option>
          </select>
          <input className="dv-input" type="number" placeholder="Precio €" value={form.price_eur} onChange={e => setForm({ ...form, price_eur: e.target.value })} />
          <select className="dv-input" value={form.status} onChange={e => setForm({ ...form, status: e.target.value })}>
            <option value="available">Disponible</option>
            <option value="reserved">Reservada</option>
            <option value="sold">Vendida</option>
          </select>
          <textarea className="dv-input sm:col-span-2" rows="2" placeholder="Descripción" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <button className="dv-btn dv-btn-primary sm:col-span-2">Guardar</button>
        </form>
      )}
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        {villas.map(v => (
          <div key={v.villa_id} className="relative" data-focus-id={v.villa_id}>
            <button
              onClick={() => navigate(`/villa/${v.villa_id}`)}
              data-testid={`villa-card-${v.villa_id}`}
              className="dv-card text-left hover:-translate-y-0.5 transition-transform w-full"
            >
              <div className="flex justify-between items-start mb-2 pr-8">
                <div>
                  <div className="text-xs text-stone-500 uppercase tracking-widest">{v.model}</div>
                  <div className="font-serif-dv text-2xl">{v.name}</div>
                </div>
                <span className={`dv-badge ${v.status === "sold" ? "dv-badge-baja" : v.status === "reserved" ? "dv-badge-media" : ""}`}>
                  {v.status === "sold" ? "Vendida" : v.status === "reserved" ? "Reservada" : "Disponible"}
                </span>
              </div>
              <div className="font-serif-dv text-3xl text-stone-800">€{Number(v.price_eur).toLocaleString()}</div>
              {v.baseline_delivery_date && (
                <div className="mt-2 text-xs text-stone-500 flex items-center gap-1.5" data-testid={`villa-baseline-${v.villa_id}`}>
                  <span className="inline-block w-1.5 h-1.5 rounded-full bg-[#3F7D58]" />
                  Baseline · entrega {new Date(v.baseline_delivery_date).toLocaleDateString("es-ES", { day: "numeric", month: "short", year: "numeric" })}
                </div>
              )}
              {v.description && <p className="text-sm text-stone-500 mt-2 line-clamp-2">{v.description}</p>}
              {v.owner_user_id && (
                <div className="mt-2 inline-flex items-center gap-1.5 text-xs px-2 py-1 rounded-full bg-emerald-50 text-emerald-800 border border-emerald-200" data-testid={`villa-owner-${v.villa_id}`}>
                  <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" />
                  Inversor: {ownerNames[v.owner_user_id] || v.owner_user_id}
                </div>
              )}
              <div className="mt-4 text-xs text-stone-400 flex items-center gap-1">
                Ver vista 360 <CaretRight size={12} />
              </div>
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setAssigningFor(v); }}
              data-testid={`villa-assign-${v.villa_id}`}
              title={v.owner_user_id ? "Reasignar inversor" : "Asignar inversor"}
              className="absolute top-3 right-12 w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600 transition"
            >
              <UserPlus size={14} />
            </button>
            <button
              onClick={(e) => { e.stopPropagation(); setEditingBaselineFor(v); }}
              data-testid={`villa-edit-baseline-${v.villa_id}`}
              title="Editar baseline"
              className="absolute top-3 right-3 w-8 h-8 rounded-full bg-stone-100 hover:bg-stone-200 flex items-center justify-center text-stone-600 transition"
            >
              <PencilSimple size={14} />
            </button>
          </div>
        ))}
      </div>

      {editingBaselineFor && (
        <BaselineEditor
          villa={editingBaselineFor}
          onClose={() => setEditingBaselineFor(null)}
          onSaved={() => { setEditingBaselineFor(null); load(); }}
        />
      )}
      {assigningFor && (
        <AssignInvestorModal
          villa={assigningFor}
          onClose={() => setAssigningFor(null)}
          onAssigned={() => { setAssigningFor(null); load(); }}
        />
      )}
    </Section>
  );
}


function BaselineEditor({ villa, onClose, onSaved }) {
  const [delivery, setDelivery] = useState(villa.baseline_delivery_date || "");
  const [income, setIncome] = useState(
    villa.baseline_annual_income_eur != null ? String(villa.baseline_annual_income_eur) : "");
  const [occupancy, setOccupancy] = useState(
    villa.baseline_occupancy_pct != null ? String(villa.baseline_occupancy_pct) : "");
  const [saving, setSaving] = useState(false);

  const hasAnyBaseline = Boolean(
    villa.baseline_delivery_date || villa.baseline_annual_income_eur != null || villa.baseline_occupancy_pct != null
  );

  const buildPayload = (clear = false) => {
    if (clear) {
      return {
        baseline_delivery_date: null,
        baseline_annual_income_eur: null,
        baseline_occupancy_pct: null,
        baseline_set_at: null,
      };
    }
    return {
      baseline_delivery_date: delivery || null,
      baseline_annual_income_eur: income === "" ? null : Number(income),
      baseline_occupancy_pct: occupancy === "" ? null : Number(occupancy),
    };
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.patch(`/villas/${villa.villa_id}`, buildPayload());
      onSaved();
    } catch (e) { alert(formatError(e)); setSaving(false); }
  };

  const clear = async () => {
    if (!window.confirm(
      `¿Limpiar el baseline de ${villa.name}?\n\n` +
      `Se borrarán fecha de entrega, ingresos anuales y ocupación. ` +
      `El inversor volverá a ver el empty state en su tab Rendimiento.`
    )) return;
    setSaving(true);
    try {
      await api.patch(`/villas/${villa.villa_id}`, buildPayload(true));
      onSaved();
    } catch (e) { alert(formatError(e)); setSaving(false); }
  };

  return (
    <div
      className="fixed inset-0 z-50 bg-black/40 flex items-center justify-center p-4"
      onClick={onClose}
      data-testid="baseline-editor-backdrop"
    >
      <div
        className="bg-white rounded-lg shadow-xl max-w-md w-full p-6 relative"
        onClick={(e) => e.stopPropagation()}
        data-testid="baseline-editor-modal"
      >
        <button
          onClick={onClose}
          data-testid="baseline-editor-close"
          className="absolute top-3 right-3 w-8 h-8 rounded-full hover:bg-stone-100 flex items-center justify-center"
        >
          <X size={16} />
        </button>

        <div className="text-xs tracking-[0.2em] uppercase text-stone-500 mb-1">Baseline · compromisos al firmar</div>
        <div className="font-serif-dv text-2xl mb-5">{villa.name}</div>

        <div className="space-y-4">
          <div>
            <label className="block text-xs text-stone-600 mb-1">Entrega prometida</label>
            <input
              type="date"
              value={delivery}
              onChange={(e) => setDelivery(e.target.value)}
              data-testid="baseline-delivery-input"
              className="dv-input w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-stone-600 mb-1">Ingresos anuales brutos (€)</label>
            <input
              type="number"
              min="0"
              step="500"
              placeholder="p.ej. 15000"
              value={income}
              onChange={(e) => setIncome(e.target.value)}
              data-testid="baseline-income-input"
              className="dv-input w-full"
            />
          </div>
          <div>
            <label className="block text-xs text-stone-600 mb-1">Ocupación objetivo (%)</label>
            <input
              type="number"
              min="0"
              max="100"
              step="5"
              placeholder="p.ej. 75"
              value={occupancy}
              onChange={(e) => setOccupancy(e.target.value)}
              data-testid="baseline-occupancy-input"
              className="dv-input w-full"
            />
          </div>
        </div>

        <div className="mt-5 pt-4 border-t border-stone-100 text-xs text-stone-500">
          Baseline fijado:{" "}
          {villa.baseline_set_at
            ? new Date(villa.baseline_set_at).toLocaleString("es-ES", {
                day: "numeric", month: "short", year: "numeric",
                hour: "2-digit", minute: "2-digit",
              })
            : "—"}
        </div>

        <div className="mt-5 flex items-center justify-between gap-2">
          <button
            onClick={clear}
            disabled={saving || !hasAnyBaseline}
            data-testid="baseline-editor-clear"
            className="dv-btn dv-btn-ghost text-red-600 disabled:opacity-40 disabled:cursor-not-allowed"
          >
            Limpiar
          </button>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              disabled={saving}
              data-testid="baseline-editor-cancel"
              className="dv-btn dv-btn-ghost"
            >
              Cancelar
            </button>
            <button
              onClick={save}
              disabled={saving}
              data-testid="baseline-editor-save"
              className="dv-btn dv-btn-primary"
            >
              {saving ? "Guardando…" : "Guardar"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function DataRoomAdmin() {
  const [folders, setFolders] = useState({});
  const [selectedCat, setSelectedCat] = useState(null);
  const [docs, setDocs] = useState([]);
  const [villas, setVillas] = useState([]);
  const [investors, setInvestors] = useState([]);
  const [form, setForm] = useState({
    title: "", category: "contracts", villa_id: "", file_url: "", description: "",
    document_date: "",
    scope: "internal", investor_user_id: "", confirm_project_all: false,
  });
  // File upload mode is the default for new docs — the URL flow is still
  // available for shared Drive/Dropbox links and preserves legacy docs.
  const [sourceMode, setSourceMode] = useState("file");
  const [file, setFile] = useState(null);
  const [showForm, setShowForm] = useState(false);

  const loadFolders = () => api.get("/documents/folders").then(r => setFolders(r.data));
  const loadDocs = (cat) => api.get("/documents", { params: { category: cat } }).then(r => setDocs(r.data));

  useEffect(() => {
    loadFolders();
    api.get("/villas").then(r => setVillas(r.data));
    api.get("/users")
      .then(r => setInvestors((r.data || []).filter(u => u.role === "investor" && u.is_active !== false)))
      .catch(() => setInvestors([]));
  }, []);

  const selectCat = (cat) => { setSelectedCat(cat); loadDocs(cat); };

  const saveDoc = async (e) => {
    e.preventDefault();
    const scope = form.scope || "internal";
    if (scope === "villa" && !form.villa_id) {
      alert("Para el alcance 'Inversores de esta villa' selecciona una villa.");
      return;
    }
    if ((scope === "investor" || scope === "investor_all_villas") && !form.investor_user_id) {
      alert("Para este alcance selecciona el inversor destinatario.");
      return;
    }
    if (scope === "project_all" && !form.confirm_project_all) {
      alert("Marca la confirmación explícita para alcance 'Todas las villas / proyecto'.");
      return;
    }
    // Guard: require a real source per selected mode. A truly empty submit
    // isn't allowed — otherwise we'd persist a title-only stub the investor
    // could see but never open.
    if (sourceMode === "file" && !file) {
      alert("Selecciona un archivo para subir.");
      return;
    }
    if (sourceMode === "url" && !form.file_url.trim()) {
      alert("Introduce la URL del archivo.");
      return;
    }
    const scopedInvestor =
      scope === "investor" || scope === "investor_all_villas" ? form.investor_user_id : null;
    const scopedVilla = scope === "villa" ? form.villa_id : null;
    try {
      const payload = {
        title: form.title,
        category: form.category,
        description: form.description,
        document_date: form.document_date || null,
        visibility_scope: scope,
        investor_user_id: scopedInvestor,
        villa_id: scopedVilla,
        confirm_project_all: scope === "project_all" ? !!form.confirm_project_all : undefined,
      };
      if (sourceMode === "file") {
        payload.file_base64 = file.base64;
        payload.file_filename = file.filename;
        payload.file_mime = file.mime;
      } else {
        payload.file_url = form.file_url.trim();
      }
      await api.post("/documents", payload);
      setShowForm(false);
      setForm({
        title: "", category: "contracts", villa_id: "", file_url: "", description: "",
        document_date: "",
        scope: "internal", investor_user_id: "", confirm_project_all: false,
      });
      setFile(null);
      setSourceMode("file");
      loadFolders();
      if (selectedCat) loadDocs(selectedCat);
    } catch (e) { alert(formatError(e)); }
  };

  const CATS = [
    { key: "contracts", label: "Contratos", icon: "📜" },
    { key: "legal", label: "Legal", icon: "⚖️" },
    { key: "licenses", label: "Licencias", icon: "🪪" },
    { key: "construction", label: "Obra", icon: "🏗️" },
    { key: "financials", label: "Financials", icon: "📊" },
    { key: "faq", label: "FAQ", icon: "💬" },
    { key: "other", label: "Otros", icon: "📎" },
  ];

  if (selectedCat) {
    return (
      <Section title={`Data room · ${CATS.find(c => c.key === selectedCat)?.label}`} subtitle={`${docs.length} documento(s)`} action={
        <button onClick={() => setSelectedCat(null)} className="dv-btn dv-btn-ghost">← Volver a carpetas</button>
      }>
        <div className="grid md:grid-cols-2 gap-3">
          {docs.map(d => (
            <DocCard key={d.doc_id} doc={d}
              onDeleted={() => { loadFolders(); loadDocs(selectedCat); }}
              onUpdated={() => { loadFolders(); loadDocs(selectedCat); }}
            />
          ))}
          {docs.length === 0 && <div className="dv-card text-stone-400 text-center col-span-2 py-8">Sin documentos en esta carpeta.</div>}
        </div>
      </Section>
    );
  }

  return (
    <Section title="Data room" subtitle="Documentos organizados por tipo. Toda acción queda registrada." action={
      <button onClick={() => setShowForm(!showForm)} className="dv-btn dv-btn-primary" data-testid="doc-new-btn"><Plus size={16} /> Subir documento</button>
    }>
      {showForm && (
        <form onSubmit={saveDoc} className="dv-card mb-5 grid sm:grid-cols-2 gap-3">
          <input className="dv-input sm:col-span-2" placeholder="Título" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required data-testid="doc-title" />
          <select className="dv-input" value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} data-testid="doc-category">
            {CATS.map(c => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {/* PR — explicit visibility scope. "Sin villa asociada" no longer
              means "all villas"; project-wide visibility requires the
              project_all scope with explicit confirmation. */}
          <select
            className="dv-input"
            value={form.scope}
            onChange={(e) => setForm({ ...form, scope: e.target.value })}
            data-testid="doc-scope"
          >
            <option value="internal">Solo equipo interno</option>
            <option value="investor">Solo este inversor</option>
            <option value="investor_all_villas">Este inversor, todas sus villas</option>
            <option value="villa">Inversores de esta villa</option>
            <option value="project_all">Todas las villas / proyecto</option>
          </select>
          {(form.scope === "investor" || form.scope === "investor_all_villas") && (
            <select
              className="dv-input sm:col-span-2"
              value={form.investor_user_id}
              onChange={(e) => setForm({ ...form, investor_user_id: e.target.value })}
              data-testid="doc-scope-investor"
              required
            >
              <option value="">— Selecciona inversor —</option>
              {investors.map((u) => (
                <option key={u.user_id} value={u.user_id}>
                  {u.name || "(sin nombre)"} — {u.email}
                </option>
              ))}
            </select>
          )}
          {form.scope === "villa" && (
            <select
              className="dv-input sm:col-span-2"
              value={form.villa_id}
              onChange={e => setForm({ ...form, villa_id: e.target.value })}
              data-testid="doc-scope-villa"
              required
            >
              <option value="">— Selecciona villa —</option>
              {villas.map(v => <option key={v.villa_id} value={v.villa_id}>{v.name}</option>)}
            </select>
          )}
          {form.scope === "project_all" && (
            <label className="sm:col-span-2 inline-flex items-start gap-2 text-xs text-amber-900 bg-amber-50 border border-amber-200 rounded px-2 py-1.5">
              <input
                type="checkbox"
                checked={!!form.confirm_project_all}
                onChange={(e) => setForm({ ...form, confirm_project_all: e.target.checked })}
                className="mt-0.5"
                data-testid="doc-scope-confirm"
              />
              <span>Confirmo que este documento será visible para todos los inversores.</span>
            </label>
          )}
          <DocumentFileInput
            mode={sourceMode}
            onModeChange={(m) => { setSourceMode(m); }}
            url={form.file_url}
            onUrlChange={(v) => setForm({ ...form, file_url: v })}
            file={file}
            onFileChange={setFile}
            onError={(msg) => alert(msg)}
            testidPrefix="dataroom-form-source"
          />
          <textarea className="dv-input sm:col-span-2" rows="2" placeholder="Descripción (opcional)" value={form.description} onChange={e => setForm({ ...form, description: e.target.value })} />
          <div className="sm:col-span-2">
            <label className="text-xs uppercase tracking-widest text-stone-500">
              Fecha del documento (opcional)
            </label>
            <input
              type="date"
              className="dv-input mt-1"
              value={form.document_date}
              onChange={e => setForm({ ...form, document_date: e.target.value })}
              data-testid="doc-document-date"
            />
            <div className="text-[11px] text-stone-500 mt-1">
              Si dejas vacío se muestra la fecha de subida.
            </div>
          </div>
          <button className="dv-btn dv-btn-primary sm:col-span-2">Publicar documento</button>
        </form>
      )}
      <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {CATS.map(c => (
          <button key={c.key} onClick={() => selectCat(c.key)} data-testid={`folder-${c.key}`}
            className="dv-card text-left hover:-translate-y-0.5 transition-transform">
            <div className="text-3xl mb-2">{c.icon}</div>
            <div className="font-serif-dv text-xl">{c.label}</div>
            <div className="text-xs text-stone-500 mt-1">{folders[c.key] || 0} documento(s)</div>
          </button>
        ))}
      </div>
    </Section>
  );
}

function DocCard({ doc, onDeleted, onUpdated }) {
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(false);
  const [editForm, setEditForm] = useState({
    title: doc.title || "",
    category: doc.category || "other",
    doc_type: doc.doc_type || "general",
    description: doc.description || "",
    document_date: doc.document_date || "",
  });
  const [views, setViews] = useState(null);
  const [busy, setBusy] = useState(false);
  const loadViews = async () => {
    try { const { data } = await api.get(`/documents/${doc.doc_id}/views`); setViews(data); } catch {}
  };
  const handleOpen = async () => {
    setOpen(true);
    try { await api.get(`/documents/${doc.doc_id}`); } catch {}
    loadViews();
  };
  const handleDelete = async () => {
    const ok = window.confirm(
      `¿Eliminar permanentemente "${doc.title}"?\n\nEsta acción es IRREVERSIBLE. ` +
      "También se eliminará el historial de visualizaciones del documento."
    );
    if (!ok) return;
    setBusy(true);
    try {
      await api.delete(`/documents/${doc.doc_id}?confirm=true`);
      setOpen(false);
      onDeleted?.();
    } catch (e) {
      alert(formatError(e));
    } finally { setBusy(false); }
  };
  const handleEditSave = async () => {
    setBusy(true);
    try {
      const update = {};
      ["title", "category", "doc_type", "description"].forEach(k => {
        if (editForm[k] !== (doc[k] || (k === "doc_type" ? "general" : k === "category" ? "other" : ""))) {
          update[k] = editForm[k];
        }
      });
      // document_date supports explicit clearing — "" sent as null.
      if ((editForm.document_date || "") !== (doc.document_date || "")) {
        update.document_date = editForm.document_date || null;
      }
      if (Object.keys(update).length === 0) { setEditing(false); return; }
      await api.patch(`/documents/${doc.doc_id}`, update);
      setEditing(false);
      setOpen(false);
      onUpdated?.();
    } catch (e) {
      alert(formatError(e));
    } finally { setBusy(false); }
  };
  const CATS = ["contracts", "legal", "licenses", "construction", "financials", "faq", "other"];
  const DOC_TYPES = ["general", "contract", "invoice", "legal", "other"];
  return (
    <>
      <button onClick={handleOpen} className="dv-card text-left flex items-center gap-4 hover:-translate-y-0.5 transition-transform">
        <div className="w-11 h-11 rounded-md bg-stone-100 flex items-center justify-center">
          <FileText size={22} weight="light" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="font-medium truncate">{doc.title}</div>
          <div className="text-xs text-stone-500">v{doc.version || 1} · {new Date(doc.document_date || doc.created_at).toLocaleDateString("es-ES")}</div>
        </div>
      </button>
      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40" onClick={() => { setOpen(false); setEditing(false); }}>
          <div className="dv-card max-w-2xl w-full bg-white" onClick={e => e.stopPropagation()} data-testid={`doc-modal-${doc.doc_id}`}>
            <div className="flex justify-between items-start mb-3">
              <div>
                <div className="text-xs uppercase tracking-widest text-stone-500">{doc.category}</div>
                <div className="font-serif-dv text-2xl">{doc.title}</div>
              </div>
              <button onClick={() => { setOpen(false); setEditing(false); }} className="text-stone-400 text-2xl leading-none">×</button>
            </div>

            {!editing && (
              <>
                {doc.description && <p className="text-stone-600 text-sm mb-4">{doc.description}</p>}
                {(doc.file_id || doc.file_url) && (
                  <a
                    href={doc.file_id
                      ? `${process.env.REACT_APP_BACKEND_URL}/api/files/${doc.file_id}`
                      : doc.file_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="dv-btn dv-btn-primary w-full mb-4"
                  >
                    Abrir documento
                  </a>
                )}
                <div className="flex gap-2 mb-4">
                  <button
                    onClick={() => setEditing(true)}
                    data-testid={`doc-edit-${doc.doc_id}`}
                    className="text-xs px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:border-stone-900 hover:bg-stone-50"
                  >
                    Editar metadatos
                  </button>
                  <button
                    onClick={handleDelete}
                    disabled={busy}
                    data-testid={`doc-delete-${doc.doc_id}`}
                    className="text-xs px-3 py-1.5 rounded border border-red-300 text-red-700 hover:bg-red-700 hover:text-white hover:border-red-700 disabled:opacity-50"
                  >
                    {busy ? "Eliminando…" : "Eliminar permanentemente"}
                  </button>
                </div>
              </>
            )}

            {editing && (
              <div className="space-y-3 mb-4" data-testid={`doc-edit-form-${doc.doc_id}`}>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Título</label>
                  <input type="text" value={editForm.title}
                         onChange={(e) => setEditForm({ ...editForm, title: e.target.value })}
                         data-testid="doc-edit-title"
                         className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none" />
                </div>
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">Categoría</label>
                    <select value={editForm.category}
                            onChange={(e) => setEditForm({ ...editForm, category: e.target.value })}
                            data-testid="doc-edit-category"
                            className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none">
                      {CATS.map(c => <option key={c} value={c}>{c}</option>)}
                    </select>
                  </div>
                  <div>
                    <label className="text-[10px] uppercase tracking-widest text-stone-500">Tipo</label>
                    <select value={editForm.doc_type}
                            onChange={(e) => setEditForm({ ...editForm, doc_type: e.target.value })}
                            data-testid="doc-edit-doctype"
                            className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none">
                      {DOC_TYPES.map(d => <option key={d} value={d}>{d}</option>)}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Descripción</label>
                  <textarea rows="3" value={editForm.description}
                            onChange={(e) => setEditForm({ ...editForm, description: e.target.value })}
                            data-testid="doc-edit-description"
                            className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none" />
                </div>
                <div>
                  <label className="text-[10px] uppercase tracking-widest text-stone-500">Fecha del documento (opcional)</label>
                  <input type="date" value={editForm.document_date}
                         onChange={(e) => setEditForm({ ...editForm, document_date: e.target.value })}
                         data-testid="doc-edit-document-date"
                         className="w-full border border-stone-300 rounded px-3 py-2 text-sm focus:border-stone-900 focus:outline-none" />
                  <div className="text-[11px] text-stone-500 mt-1">
                    Vacío → se muestra la fecha de subida.
                  </div>
                </div>
                <div className="flex gap-2 justify-end">
                  <button onClick={() => setEditing(false)} disabled={busy}
                          className="text-sm px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
                    Cancelar
                  </button>
                  <button onClick={handleEditSave} disabled={busy}
                          data-testid="doc-edit-submit"
                          className="text-sm px-3 py-1.5 rounded bg-stone-900 text-white hover:bg-stone-800 disabled:opacity-50">
                    {busy ? "Guardando…" : "Guardar"}
                  </button>
                </div>
              </div>
            )}
            {views && (
              <div className="pt-4 border-t border-stone-200">
                <div className="text-xs uppercase tracking-widest text-stone-500 mb-2">
                  Tracking de vistas
                </div>
                <div className="text-sm text-stone-600 mb-2">{views.total} vistas · {views.unique_users} usuarios únicos</div>
                <div className="max-h-40 overflow-auto">
                  {views.views.slice(0, 10).map((v, i) => (
                    <div key={i} className="text-xs py-1 flex justify-between text-stone-500 border-b border-stone-100 last:border-0">
                      <span>{v.user_name} ({v.user_role})</span>
                      <span>{new Date(v.viewed_at).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}</span>
                    </div>
                  ))}
                  {views.views.length === 0 && <div className="text-stone-400 text-xs text-center py-2">Nadie lo ha abierto aún.</div>}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
}

function ActivityLog() {
  const [items, setItems] = useState([]);
  useEffect(() => { api.get("/activity").then(r => setItems(r.data)); }, []);

  const actionLabels = {
    "villa.created": "Creó villa",
    "villa.updated": "Actualizó villa",
    "progress.added": "Subió avance de obra",
    "document.uploaded": "Subió documento",
    "payment.updated": "Actualizó pago",
    "lead.updated": "Actualizó lead",
    "user.role_changed": "Cambió rol de usuario",
    "settings.auto_reply.updated": "Cambió Auto Reply",
  };

  return (
    <Section title="Registro de actividad" subtitle="Quién hizo qué y cuándo. Última actividad arriba.">
      {items.length === 0 ? (
        <div className="dv-card text-center py-10 text-stone-400">Sin actividad registrada aún.</div>
      ) : (
        <div className="dv-card p-0 overflow-hidden">
          <div className="overflow-x-auto">
          <table className="w-full text-sm min-w-[600px]">
            <thead className="bg-stone-50 border-b border-stone-200 text-left">
              <tr className="text-xs uppercase tracking-widest text-stone-500">
                <th className="px-4 py-3">Fecha</th>
                <th className="px-4 py-3">Usuario</th>
                <th className="px-4 py-3">Acción</th>
                <th className="px-4 py-3">Detalle</th>
              </tr>
            </thead>
            <tbody>
              {items.map(a => (
                <tr key={a.activity_id} className="border-b border-stone-100">
                  <td className="px-4 py-3 text-stone-500 whitespace-nowrap">
                    {new Date(a.timestamp).toLocaleString("es-ES", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" })}
                  </td>
                  <td className="px-4 py-3">
                    <div className="font-medium">{a.actor_name}</div>
                    <div className="text-xs text-stone-400 capitalize">{a.actor_role}</div>
                  </td>
                  <td className="px-4 py-3">{actionLabels[a.action] || a.action}</td>
                  <td className="px-4 py-3 text-stone-500 text-xs">
                    {a.meta && Object.entries(a.meta).slice(0, 3).map(([k, v]) => (
                      <div key={k}><span className="text-stone-400">{k}:</span> {String(v).slice(0, 60)}</div>
                    ))}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
          </div>
        </div>
      )}
    </Section>
  );
}

function UsersAdmin() {
  const { user: me } = useAuth();
  const [users, setUsers] = useState([]);
  const [busyId, setBusyId] = useState(null);
  const [editingNameId, setEditingNameId] = useState(null);
  const [nameDraft, setNameDraft] = useState("");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const load = () => api.get("/users").then(r => setUsers(r.data));
  useEffect(() => { load(); }, []);

  const changeRole = async (u, role) => {
    await api.patch(`/users/${u.user_id}/role`, { role });
    load();
  };

  const startEditName = (u) => { setEditingNameId(u.user_id); setNameDraft(u.name || ""); };
  const cancelEditName = () => { setEditingNameId(null); setNameDraft(""); };
  const saveName = async (u) => {
    const next = nameDraft.trim();
    if (!next || next === u.name) { cancelEditName(); return; }
    setBusyId(u.user_id);
    try {
      await api.patch(`/users/${u.user_id}`, { name: next });
      cancelEditName();
      await load();
    } catch (e) { alert(formatError(e)); }
    finally { setBusyId(null); }
  };

  const toggleActive = async (u) => {
    const currentlyActive = u.is_active !== false; // legacy undefined = active
    const nextActive = !currentlyActive;
    const confirmMsg = nextActive
      ? `Activar a ${u.name || u.email}?\n\nEste usuario recuperará acceso a la plataforma.`
      : `Desactivar a ${u.name || u.email}?\n\nEste usuario perderá acceso inmediato a la plataforma.`;
    if (!window.confirm(confirmMsg)) return;
    setBusyId(u.user_id);
    try {
      await api.patch(`/users/${u.user_id}/status`, { is_active: nextActive });
      await load();
    } catch (e) {
      alert(formatError(e));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <Section title="Usuarios de la plataforma">
      <div className="dv-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[760px]">
          <thead className="bg-stone-50 border-b border-stone-200 text-left">
            <tr className="text-xs tracking-wider uppercase text-stone-500">
              <th className="px-4 py-3">Nombre</th><th className="px-4 py-3">Email</th>
              <th className="px-4 py-3">Rol</th><th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3">Proveedor</th>
              <th className="px-4 py-3">Desde</th>
              <th className="px-4 py-3 text-right">Acciones</th>
            </tr>
          </thead>
          <tbody>
            {users.map(u => {
              const active = u.is_active !== false;
              const isSelf = me && me.user_id === u.user_id;
              const busy = busyId === u.user_id;
              return (
                <tr key={u.user_id} className="border-b border-stone-100" data-testid={`user-row-${u.user_id}`}>
                  <td className="px-4 py-3 font-medium">
                    {editingNameId === u.user_id ? (
                      <div className="flex items-center gap-1">
                        <input
                          type="text"
                          value={nameDraft}
                          onChange={(e) => setNameDraft(e.target.value)}
                          onKeyDown={(e) => {
                            if (e.key === "Enter") saveName(u);
                            if (e.key === "Escape") cancelEditName();
                          }}
                          autoFocus
                          data-testid={`user-name-input-${u.user_id}`}
                          className="text-sm border border-stone-300 rounded px-2 py-1 focus:border-stone-900 focus:outline-none"
                        />
                        <button
                          onClick={() => saveName(u)}
                          disabled={busy}
                          data-testid={`user-name-save-${u.user_id}`}
                          className="text-xs px-2 py-0.5 rounded border border-stone-900 bg-stone-900 text-white hover:bg-stone-800"
                        >OK</button>
                        <button
                          onClick={cancelEditName}
                          className="text-xs px-2 py-0.5 rounded border border-stone-300 text-stone-500 hover:bg-stone-50"
                        >×</button>
                      </div>
                    ) : (
                      <button
                        onClick={() => startEditName(u)}
                        data-testid={`user-name-edit-${u.user_id}`}
                        title="Editar nombre"
                        className="text-left hover:underline decoration-dotted decoration-stone-400 underline-offset-4"
                      >
                        {u.name || <span className="text-stone-400 italic">(sin nombre)</span>}
                      </button>
                    )}
                  </td>
                  <td className="px-4 py-3 text-stone-600">{u.email}</td>
                  <td className="px-4 py-3">
                    <select value={u.role} onChange={e => changeRole(u, e.target.value)} className="text-xs border border-stone-200 rounded px-2 py-1" data-testid={`user-role-${u.user_id}`}>
                      <option value="investor">Inversor</option>
                      <option value="constructor">Constructor</option>
                      <option value="marketing">Marketing</option>
                      <option value="admin">Admin</option>
                    </select>
                  </td>
                  <td className="px-4 py-3">
                    <span
                      className={`dv-badge ${active ? "dv-badge-baja" : "dv-badge-alta"}`}
                      data-testid={`user-status-${u.user_id}`}
                    >
                      {active ? "Activo" : "Inactivo"}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-stone-500 text-xs capitalize">{u.provider || "email"}</td>
                  <td className="px-4 py-3 text-stone-500 text-xs">{u.created_at ? new Date(u.created_at).toLocaleDateString("es-ES") : "—"}</td>
                  <td className="px-4 py-3 text-right">
                    <button
                      onClick={() => toggleActive(u)}
                      disabled={isSelf || busy}
                      title={isSelf ? "No puedes desactivarte a ti mismo" : (active ? "Desactivar usuario" : "Activar usuario")}
                      data-testid={`user-toggle-${u.user_id}`}
                      className={`text-xs px-3 py-1 rounded border transition-all ${
                        isSelf
                          ? "border-stone-200 text-stone-300 cursor-not-allowed"
                          : active
                            ? "border-stone-300 text-stone-700 hover:bg-stone-900 hover:text-white hover:border-stone-900"
                            : "border-emerald-300 text-emerald-700 hover:bg-emerald-700 hover:text-white hover:border-emerald-700"
                      }`}
                    >
                      {busy ? "…" : active ? "Desactivar" : "Activar"}
                    </button>
                    <button
                      onClick={() => setDeleteTarget(u)}
                      disabled={isSelf || busy}
                      title={isSelf ? "No puedes eliminarte a ti mismo" : "Eliminar usuario permanentemente"}
                      data-testid={`user-delete-${u.user_id}`}
                      className={`ml-2 text-xs px-3 py-1 rounded border transition-all ${
                        isSelf
                          ? "border-stone-200 text-stone-300 cursor-not-allowed"
                          : "border-red-300 text-red-700 hover:bg-red-700 hover:text-white hover:border-red-700"
                      }`}
                    >
                      Eliminar
                    </button>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
        </div>
      </div>
      {deleteTarget && (
        <DeleteUserModal
          target={deleteTarget}
          onClose={() => setDeleteTarget(null)}
          onDone={async () => { setDeleteTarget(null); await load(); }}
        />
      )}
    </Section>
  );
}

/**
 * DeleteUserModal — admin-only modal that fetches an impact preview before
 * letting the admin hard-delete a user. Surfaces blockers (self-delete,
 * last admin, owns villas) and side-effects (orphaned payments, leads, etc.).
 */
function DeleteUserModal({ target, onClose, onDone }) {
  const [impact, setImpact] = useState(null);
  const [err, setErr] = useState("");
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    setImpact(null); setErr("");
    api.get(`/users/${target.user_id}/impact`)
      .then(r => { if (!cancelled) setImpact(r.data); })
      .catch(e => { if (!cancelled) setErr(formatError(e)); });
    return () => { cancelled = true; };
  }, [target.user_id]);

  const submit = async () => {
    setBusy(true); setErr("");
    try {
      await api.delete(`/users/${target.user_id}?confirm=true`);
      await onDone?.();
    } catch (e) {
      setErr(formatError(e));
    } finally { setBusy(false); }
  };

  const SE_LABELS = {
    payments_total: "Pagos asociados",
    payments_paid: "Pagos ya pagados",
    leads_assigned: "Leads asignados",
    documents_uploaded: "Documentos subidos",
    tasks_assigned: "Tareas asignadas",
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/40"
         data-testid="delete-user-modal"
         onClick={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div className="dv-card max-w-lg w-full !p-6 bg-white">
        <h3 className="font-serif-dv text-2xl mb-1">Eliminar usuario</h3>
        <p className="text-sm text-stone-600 mb-4">
          {target.name} · <span className="text-stone-500">{target.email}</span> · <span className="capitalize">{target.role}</span>
        </p>

        {!impact && !err && (
          <div className="text-sm text-stone-500 py-6 text-center">Calculando impacto…</div>
        )}

        {impact && (
          <>
            {impact.blockers && impact.blockers.length > 0 && (
              <div className="mb-4 p-3 bg-red-50 border border-red-200 rounded text-sm">
                <div className="font-medium text-red-900 mb-1">No se puede eliminar:</div>
                <ul className="list-disc pl-5 space-y-1 text-red-800">
                  {impact.blockers.map(b => (
                    <li key={b.code} data-testid={`blocker-${b.code}`}>{b.message}</li>
                  ))}
                </ul>
              </div>
            )}

            <div className="mb-4">
              <div className="text-[10px] uppercase tracking-widest text-stone-500 mb-2">
                Datos asociados (se conservarán como histórico, sin propietario)
              </div>
              <div className="grid grid-cols-2 gap-2 text-sm">
                {Object.entries(impact.side_effects || {})
                  .filter(([k]) => k !== "villas_owned")
                  .map(([k, v]) => (
                    <div key={k} className="flex justify-between border border-stone-100 rounded px-2 py-1">
                      <span className="text-stone-500">{SE_LABELS[k] || k}</span>
                      <span className="font-medium">{v}</span>
                    </div>
                  ))}
              </div>
            </div>

            <div className="text-xs text-stone-500 mb-4 p-2 bg-amber-50 border border-amber-200 rounded">
              Esta acción es <b>irreversible</b>. Las sesiones activas del usuario se cerrarán y dejará de poder iniciar sesión.
              Los datos históricos (pagos, leads, documentos) se mantienen para auditoría.
            </div>
          </>
        )}

        {err && <div className="bg-red-50 border border-red-200 text-red-800 text-sm rounded p-2 mb-3">{err}</div>}

        <div className="flex items-center justify-end gap-2">
          <button onClick={onClose} disabled={busy}
                  className="text-sm px-3 py-1.5 rounded border border-stone-300 text-stone-700 hover:bg-stone-50">
            Cancelar
          </button>
          <button onClick={submit}
                  disabled={busy || !impact || !impact.can_delete}
                  data-testid="delete-user-submit"
                  className="text-sm px-3 py-1.5 rounded bg-red-700 text-white hover:bg-red-800 disabled:opacity-50">
            {busy ? "Eliminando…" : "Eliminar definitivamente"}
          </button>
        </div>
      </div>
    </div>
  );
}

function PaymentsAdmin() {
  const [payments, setPayments] = useState([]);
  const [users, setUsers] = useState([]);
  const [villas, setVillas] = useState([]);
  const [showForm, setShowForm] = useState(false);
  // Archived payments hidden by default so the table matches what the investor
  // sees. Admin can flip the toggle to audit history without hard-deleting.
  const [showArchived, setShowArchived] = useState(false);
  const [showOnlyDuplicates, setShowOnlyDuplicates] = useState(false);
  const [form, setForm] = useState({ investor_user_id: "", villa_id: "", phase: "Fase 1 - Reserva", amount: 9900, currency: "EUR", due_date: "", status: "pending" });

  const load = () => api.get("/payments").then(r => setPayments(r.data));
  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data.filter(u => u.role === "investor")));
    api.get("/villas").then(r => setVillas(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/payments", { ...form, amount: Number(form.amount) });
      setShowForm(false); load();
    } catch (e) { alert(formatError(e)); }
  };

  const setStatus = async (p, status) => { await api.patch(`/payments/${p.payment_id}`, { status }); load(); };

  // Soft-archive via POST /payments/{id}/archive. Backend returns 409 with
  // detail.code="paid_payment_requires_force" when the payment is already
  // marked as paid; re-confirm and retry with ?force=true so admin doesn't
  // have to bounce to another tab to remove a duplicated paid entry.
  const archive = async (p) => {
    if (!window.confirm("¿Archivar este pago? Se ocultará al inversor pero se conservará el histórico.")) return;
    try {
      await api.post(`/payments/${p.payment_id}/archive`, {});
      load();
    } catch (e) {
      const code = e?.response?.data?.detail?.code;
      if (code === "paid_payment_requires_force") {
        if (window.confirm("Este pago está marcado como pagado. ¿Archivar de todas formas? Se conservará el histórico contable.")) {
          try {
            await api.post(`/payments/${p.payment_id}/archive?force=true`, {});
            load();
          } catch (err) { alert(formatError(err)); }
        }
      } else {
        alert(formatError(e));
      }
    }
  };

  const archivedCount = useMemo(
    () => payments.filter((p) => p.status === "archived").length,
    [payments]
  );
  const { dupSet, dupCount } = useDuplicatePayments(payments);
  const visiblePayments = useMemo(() => {
    let out = showArchived ? payments : payments.filter((p) => p.status !== "archived");
    if (showOnlyDuplicates) {
      out = out.filter((p) => dupSet.has(p.payment_id));
    }
    return out;
  }, [payments, showArchived, showOnlyDuplicates, dupSet]);

  return (
    <Section title="Pagos de inversores" action={
      <div className="flex items-center gap-2 flex-wrap">
        <label className="flex items-center gap-1.5 text-xs text-stone-600">
          <input
            type="checkbox"
            checked={showArchived}
            onChange={(e) => setShowArchived(e.target.checked)}
            data-testid="payments-admin-toggle-archived"
          />
          Mostrar archivados
        </label>
        <button onClick={() => setShowForm(!showForm)} className="dv-btn dv-btn-primary"><Plus size={16} /> Nuevo pago</button>
      </div>
    }>
      {!showArchived && archivedCount > 0 && (
        <div className="mb-3 text-xs text-stone-500 flex items-center gap-2" data-testid="payments-admin-archived-hint">
          <Archive size={12} />
          {archivedCount} pago{archivedCount === 1 ? "" : "s"} archivado{archivedCount === 1 ? "" : "s"} oculto{archivedCount === 1 ? "" : "s"} ·{" "}
          <button
            type="button"
            className="underline hover:text-stone-800"
            onClick={() => setShowArchived(true)}
          >
            Mostrar
          </button>
        </div>
      )}
      {dupCount > 0 && (
        <div
          className="mb-3 flex items-center justify-between gap-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900"
          data-testid="payments-admin-duplicate-banner"
        >
          <div>
            Hay <b>{dupCount}</b> posibles pagos duplicados. Revisa antes de enviar al inversor.
          </div>
          <label className="inline-flex items-center gap-1.5 whitespace-nowrap">
            <input
              type="checkbox"
              checked={showOnlyDuplicates}
              onChange={(e) => setShowOnlyDuplicates(e.target.checked)}
              data-testid="payments-admin-toggle-duplicates"
            />
            Solo duplicados
          </label>
        </div>
      )}
      {showForm && (
        <form onSubmit={save} className="dv-card mb-5 grid sm:grid-cols-2 gap-3">
          <select className="dv-input" value={form.investor_user_id} onChange={e => setForm({ ...form, investor_user_id: e.target.value })} required>
            <option value="">Inversor…</option>
            {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name} ({u.email})</option>)}
          </select>
          <select className="dv-input" value={form.villa_id} onChange={e => setForm({ ...form, villa_id: e.target.value })} required>
            <option value="">Villa…</option>
            {villas.map(v => <option key={v.villa_id} value={v.villa_id}>{v.name}</option>)}
          </select>
          <input className="dv-input" placeholder="Fase (ej: Fase 1 - Reserva)" value={form.phase} onChange={e => setForm({ ...form, phase: e.target.value })} />
          <input className="dv-input" type="number" placeholder="Importe" value={form.amount} onChange={e => setForm({ ...form, amount: e.target.value })} />
          <input className="dv-input" type="date" value={form.due_date} onChange={e => setForm({ ...form, due_date: e.target.value })} required />
          <button className="dv-btn dv-btn-primary sm:col-span-2">Crear</button>
        </form>
      )}
      <div className="dv-card p-0 overflow-hidden">
        <div className="overflow-x-auto">
        <table className="w-full text-sm min-w-[680px]">
          <thead className="bg-stone-50 border-b border-stone-200 text-left">
            <tr className="text-xs tracking-wider uppercase text-stone-500">
              <th className="px-4 py-3">Inversor</th><th className="px-4 py-3">Villa</th>
              <th className="px-4 py-3">Concepto</th><th className="px-4 py-3">Vencimiento</th>
              <th className="px-4 py-3 text-right">Importe</th><th className="px-4 py-3">Estado</th>
              <th className="px-4 py-3 text-right">Acción</th>
            </tr>
          </thead>
          <tbody>
            {visiblePayments.map(p => {
              const u = users.find(x => x.user_id === p.investor_user_id);
              const v = villas.find(x => x.villa_id === p.villa_id);
              const isArchived = p.status === "archived";
              const isDup = dupSet.has(p.payment_id);
              return (
                <tr
                  key={p.payment_id}
                  className={`border-b border-stone-100 ${isArchived ? "opacity-60" : ""}`}
                  data-testid={`payments-admin-row-${p.payment_id}`}
                >
                  <td className="px-4 py-3">{u?.name || p.investor_user_id}</td>
                  <td className="px-4 py-3 text-stone-600">{v?.name || p.villa_id}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2 flex-wrap">
                      <span>{paymentLabel(p)}</span>
                      {isDup && (
                        <span
                          className="inline-flex text-[10px] px-1.5 py-0.5 rounded-full border bg-amber-50 text-amber-800 border-amber-200"
                          title="Otro pago activo tiene el mismo inversor, villa, fase, vencimiento e importe."
                          data-testid={`payments-admin-duplicate-badge-${p.payment_id}`}
                        >
                          Posible duplicado
                        </span>
                      )}
                    </div>
                  </td>
                  <td className="px-4 py-3">{p.due_date}</td>
                  <td className="px-4 py-3 text-right font-serif-dv text-lg">{Number(p.amount).toLocaleString()} {p.currency}</td>
                  <td className="px-4 py-3">
                    {isArchived ? (
                      <span className="inline-flex text-[11px] px-2 py-0.5 rounded-full border bg-stone-100 text-stone-500 border-stone-200">
                        Archivado
                      </span>
                    ) : (
                      <select value={p.status} onChange={e => setStatus(p, e.target.value)} className="text-xs border border-stone-200 rounded px-2 py-1">
                        <option value="pending">Pendiente</option>
                        <option value="paid">Pagado</option>
                        <option value="overdue">Vencido</option>
                      </select>
                    )}
                  </td>
                  <td className="px-4 py-3 text-right whitespace-nowrap">
                    {!isArchived && (
                      <button
                        type="button"
                        onClick={() => archive(p)}
                        className="text-xs px-2 py-1 rounded border border-stone-300 text-stone-600 hover:bg-stone-100 inline-flex items-center gap-1"
                        data-testid={`payments-admin-archive-${p.payment_id}`}
                        title="Se ocultará al inversor pero se conservará el histórico."
                      >
                        <Archive size={12} /> Archivar pago
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
            {visiblePayments.length === 0 && <tr><td colSpan="7" className="px-4 py-8 text-center text-stone-400">No hay pagos registrados.</td></tr>}
          </tbody>
        </table>
        </div>
      </div>
    </Section>
  );
}

function DocumentsAdmin() {
  const [docs, setDocs] = useState([]);
  const [users, setUsers] = useState([]);
  const [form, setForm] = useState({ title: "", doc_type: "contract", investor_user_id: "", file_url: "" });
  const load = () => api.get("/documents").then(r => setDocs(r.data));

  useEffect(() => {
    load();
    api.get("/users").then(r => setUsers(r.data.filter(u => u.role === "investor")));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    try {
      await api.post("/documents", form);
      setForm({ title: "", doc_type: "contract", investor_user_id: "", file_url: "" });
      load();
    } catch (e) { alert(formatError(e)); }
  };

  return (
    <Section title="Documentos compartidos">
      <form onSubmit={save} className="dv-card mb-5 grid sm:grid-cols-2 gap-3">
        <input className="dv-input" placeholder="Título del documento" value={form.title} onChange={e => setForm({ ...form, title: e.target.value })} required />
        <select className="dv-input" value={form.doc_type} onChange={e => setForm({ ...form, doc_type: e.target.value })}>
          <option value="contract">Contrato</option>
          <option value="legal">Legal</option>
          <option value="invoice">Factura</option>
          <option value="other">Otro</option>
        </select>
        <select className="dv-input" value={form.investor_user_id} onChange={e => setForm({ ...form, investor_user_id: e.target.value })}>
          <option value="">Compartir con todos los inversores</option>
          {users.map(u => <option key={u.user_id} value={u.user_id}>{u.name}</option>)}
        </select>
        <input className="dv-input" placeholder="URL del archivo (ej: Drive)" value={form.file_url} onChange={e => setForm({ ...form, file_url: e.target.value })} />
        <button className="dv-btn dv-btn-primary sm:col-span-2">Publicar documento</button>
      </form>

      <div className="space-y-3">
        {docs.map(d => {
          const u = users.find(x => x.user_id === d.investor_user_id);
          return (
            <div key={d.doc_id} className="dv-card flex items-center gap-4">
              <div className="w-10 h-10 rounded-md bg-stone-100 flex items-center justify-center"><FileText size={20} /></div>
              <div className="flex-1">
                <div className="font-medium">{d.title}</div>
                <div className="text-xs text-stone-500">{d.doc_type} · {u?.name || "Todos los inversores"} · {new Date(d.created_at).toLocaleDateString("es-ES")}</div>
              </div>
              {d.file_url && <a href={d.file_url} target="_blank" rel="noopener noreferrer" className="dv-btn dv-btn-ghost">Ver</a>}
            </div>
          );
        })}
      </div>
    </Section>
  );
}

function Integrations() {
  return (
    <Section title="Integraciones externas" subtitle="Conecta tus herramientas para unificar leads y comunicación.">
      <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
        <IntegrationCard icon={GoogleLogo} name="Gmail" status="disconnected" desc="Leer y enviar correos directamente. Pendiente de habilitar API." />
        <IntegrationCard icon={WhatsappLogo} name="WhatsApp Business" status="disconnected" desc="Requiere cuenta Meta Business verificada." />
        <IntegrationCard icon={FileText} name="Google Sheets" status="disconnected" desc="Importa leads automáticamente desde tu hoja." />
        <IntegrationCard icon={EnvelopeSimple} name="Webhook del formulario web" status="active" desc={`POST ${process.env.REACT_APP_BACKEND_URL}/api/leads/public`} />
      </div>
      <div className="dv-card mt-6 bg-amber-50 border-amber-200">
        <div className="flex gap-3 items-start">
          <div className="text-amber-700 mt-1">💡</div>
          <div>
            <div className="font-medium text-amber-900">Siguiente paso — conectar Gmail / WhatsApp</div>
            <p className="text-sm text-amber-800 mt-1">
              Cuando obtengas acceso a la API de Gmail (OAuth) y a Meta Business, compartimos credenciales y activamos estas integraciones.
              Mientras tanto, el webhook público ya puede recibir leads del formulario de <strong>dolcevillaslombok.com</strong>.
            </p>
          </div>
        </div>
      </div>
    </Section>
  );
}

function IntegrationCard({ icon: Icon, name, status, desc }) {
  const isActive = status === "active";
  return (
    <div className="dv-card">
      <div className="flex items-center gap-3 mb-3">
        <div className="w-10 h-10 rounded-md bg-stone-100 flex items-center justify-center"><Icon size={22} weight="light" /></div>
        <div>
          <div className="font-medium">{name}</div>
          <span className={`dv-badge ${isActive ? "dv-badge-baja" : ""}`}>{isActive ? "Activa" : "Pendiente"}</span>
        </div>
      </div>
      <p className="text-sm text-stone-500 break-all">{desc}</p>
    </div>
  );
}
